import { gql } from "@apollo/client";

import {
  GENERAL_FIELDS,
  SITE_FIELDS,
  BUTTON_RECORD,
  BUTTON_EXTERNAL_RECORD,
  DOWNLOAD_BUTTON_RECORD,
  HUBSPOT_BUTTON_RECORD,
  PAGE_SETTINGS,
  FOOTER_OVERRIDES,
} from "./shared";
import { SERVICE_FIELDS, SERVICE_HERO_LOGO, SERVICE_OVERVIEW_FIELDS } from "./fragments/services";
import { OFFICE_FIELDS, OFFICE_INTRO_TEASER_IMAGE } from "./offices";
import { SECTIONS_WRAPPER_QUERY } from "./fragments/sections";
import { RELATED_CONTENT_SECTION_QUERY } from "./fragments/related-content";

// Service
export const SERVICE_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${SERVICE_FIELDS}
  ${SERVICE_HERO_LOGO}
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  ${PAGE_SETTINGS}
  ${OFFICE_FIELDS}
  ${OFFICE_INTRO_TEASER_IMAGE}
  ${SECTIONS_WRAPPER_QUERY}
  ${FOOTER_OVERRIDES}
  ${RELATED_CONTENT_SECTION_QUERY}
  query GetService($slug: String!, $locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: service(filter: { slug: { eq: $slug } }, locale: $locale) {
      # Service Meta (SEO)
      parent {
        slugs: _allSlugLocales(locale: $locale) {
          locale
          value
        }
        titles: _allTitleLocales(locale: $locale) {
          locale
          value
        }
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing
      __typename

      # Service Fields (Content)
      ...ServiceFields

      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      ...ServiceHeroLogo
      footerOverrides {
        ...FooterOverrides
      }
      pageSettings {
        ...PageSettings
      }
      callToActions {
        ...ButtonBlock
        ...ButtonExternalBlock
        ...DownloadButtonBlock
        ...HubspotButtonBlock
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
      sections {
        ...SectionWrapperQuery
      }
    }
    offices: allOffices(
      locale: $locale
      first: 3
      orderBy: _createdAt_DESC
      filter: {
        parent: { exists: "false" }
        blockSearchIndexing: { eq: "false" }
        _locales: { allIn: [$locale] }
        hideOfficeFromOverview: { eq: false }
      }
    ) {
      ...OfficeFields
      ...OfficeIntroTeaserImage
    }
  }
`;

export const SERVICES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${SERVICE_OVERVIEW_FIELDS}
  ${SERVICE_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${FOOTER_OVERRIDES}
  ${RELATED_CONTENT_SECTION_QUERY}
  query GetServices($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: servicesOverview(locale: $locale) {
      _locales
      # Services Overview Meta (SEO)
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Services Overview Fields (Content)
      ...ServiceOverviewFields

      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      footerOverrides {
        ...FooterOverrides
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
    }
    services: allServices(
      locale: $locale
      first: 100
      orderBy: position_ASC
      filter: { _locales: { allIn: [$locale] }, parent: { exists: "false" } }
    ) {
      ...ServiceFields
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
  }
`;

export const ALL_SERVICE_SLUGS_QUERY = gql`
  query GetAllServiceSlugs($locale: SiteLocale!) {
    allServices(locale: $locale, first: 100) {
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }
    }
  }
`;

export const ACCOUNTANCIES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${HUBSPOT_BUTTON_RECORD}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${RELATED_CONTENT_SECTION_QUERY}
  query GetAccountancies($locale: SiteLocale!, $fallbackLocales: [SiteLocale!]) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: accountanciesOverview(locale: $locale, fallbackLocales: $fallbackLocales) {
      _locales
      # Accountancies Overview Meta (SEO)
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Accountancies Overview Fields (Content)
      title
      shortTitle
      intro {
        value
      }
      shortIntro {
        value
      }
      # Accountancies Overview Intro Image
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      callToActions {
        ...ButtonBlock
        ...ButtonExternalBlock
        ...DownloadButtonBlock
        ...HubspotButtonBlock
      }
      # Accountancies Overview Components
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
    }
  }
`;

export const TAXES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${RELATED_CONTENT_SECTION_QUERY}
  query GetTaxes($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: taxesOverview(locale: $locale) {
      # Taxes Overview Meta (SEO)
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Taxes Overview Fields (Content)
      title
      shortTitle
      smallIntro
      intro {
        value
      }
      shortIntro {
        value
      }
      callToActions {
        ...ButtonBlock
        ...ButtonExternalBlock
        ...DownloadButtonBlock
        ...HubspotButtonBlock
      }
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      # Taxes Overview Components
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      relatedContentSection {
        ...RelatedContentSectionQuery
      }
    }
  }
`;

export const AUDIT_PAGE_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${FOOTER_OVERRIDES}
  query GetAuditPage($slug: String!, $locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    overview: taxesOverview(locale: $locale) {
      titles: _allShortTitleLocales(locale: $locale) {
        locale
        value
      }
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }
    }
    fields: auditPage(filter: { slug: { eq: $slug } }, locale: $locale) {
      # Audit Page Meta (SEO)
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Audit Page Fields (Content)
      id
      slug
      title
      shortTitle
      intro {
        value
      }
      titles: _allTitleLocales(locale: $locale) {
        locale
        value
      }
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }

      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      callToActions {
        ...ButtonBlock
        ...ButtonExternalBlock
        ...HubspotButtonBlock
      }

      footerOverrides {
        ...FooterOverrides
      }
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
    }
  }
`;

export const ALL_AUDIT_PAGE_SLUGS_QUERY = gql`
  query GetAllAuditPageSlugs($locale: SiteLocale!) {
    allAuditPages(locale: $locale, first: 100, filter: { blockSearchIndexing: { eq: "false" } }) {
      slugs: _allSlugLocales(locale: $locale) {
        locale
        value
      }
    }
  }
`;
